export const ME = 'me'
export const PREFERRED_SIGNUPS = 7

export function uid() {
  return Math.random().toString(36).slice(2, 10)
}

export function initialsFrom(name) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'HO'
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase()
  return parts.slice(0, 3).map((part) => part[0]).join('').toUpperCase()
}

export function hoursFromNow(hours) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString()
}

export function atDayHour(dayOffset, hour, minute = 0) {
  const date = new Date()
  date.setDate(date.getDate() + dayOffset)
  date.setHours(hour, minute, 0, 0)
  return date.toISOString()
}

export function hasStarted(iso) {
  return new Date(iso).getTime() <= Date.now()
}

export function toDateTimeLocal(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function meetingBeforeKickoff(kickoffLocal, minutes = 45) {
  if (!kickoffLocal) return ''
  const start = new Date(kickoffLocal)
  if (Number.isNaN(start.getTime())) return ''
  start.setMinutes(start.getMinutes() - minutes)
  return toDateTimeLocal(start.toISOString())
}

export function toMeetingLocal(meeting, kickoff) {
  if (!meeting) return ''
  if (/^\d{2}:\d{2}$/.test(meeting)) {
    const base = kickoff ? new Date(kickoff) : new Date()
    if (Number.isNaN(base.getTime())) return ''
    const [hours, minutes] = meeting.split(':').map(Number)
    base.setHours(hours, minutes, 0, 0)
    return toDateTimeLocal(base.toISOString())
  }
  return toDateTimeLocal(meeting)
}

export function formatKickoff(iso) {
  return new Intl.DateTimeFormat('da-DK', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function calendarStamp(date) {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function meetingIso(meeting, kickoff) {
  if (!meeting) return null
  if (/^\d{2}:\d{2}$/.test(meeting)) {
    const base = new Date(kickoff)
    if (Number.isNaN(base.getTime())) return null
    const [hours, minutes] = meeting.split(':').map(Number)
    base.setHours(hours, minutes, 0, 0)
    return base.toISOString()
  }
  const date = new Date(meeting)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function escapeIcs(value) {
  return String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
}

export function matchCalendarEvent(team, match) {
  const kickoff = new Date(match.kickoff)
  if (Number.isNaN(kickoff.getTime())) return null
  const meeting = meetingIso(match.meeting, match.kickoff)
  const start = meeting ? new Date(meeting) : kickoff
  const end = new Date(Math.max(kickoff.getTime() + 2 * 60 * 60 * 1000, start.getTime() + 90 * 60 * 1000))
  const title = `${team.name}: ${match.home ? 'Hjemme' : 'Ude'} mod ${match.opponent}`
  const lines = [
    meeting ? `Mødetid: ${formatKickoff(meeting)}` : null,
    `Kampstart: ${formatKickoff(match.kickoff)}`,
    match.note || null,
    typeof window !== 'undefined' ? `${window.location.origin}/hold/${team.id}/kamp/${match.id}` : null,
  ].filter(Boolean)
  return {
    title,
    location: match.place || '',
    description: lines.join('\n'),
    start,
    end,
    fileName: `${team.name}-${match.opponent}`.replace(/[^\wæøåÆØÅ-]+/gi, '-').toLowerCase(),
  }
}

export function eventCalendarEvent(team, event) {
  const start = new Date(event.kickoff)
  if (Number.isNaN(start.getTime())) return null
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000)
  const title = `${team.name}: ${event.title}`
  const lines = [
    `Tidspunkt: ${formatKickoff(event.kickoff)}`,
    event.description || null,
    typeof window !== 'undefined' ? `${window.location.origin}/hold/${team.id}/arrangement/${event.id}` : null,
  ].filter(Boolean)
  return {
    title,
    location: event.place || '',
    description: lines.join('\n'),
    start,
    end,
    fileName: `${team.name}-${event.title}`.replace(/[^\wæøåÆØÅ-]+/gi, '-').toLowerCase(),
  }
}

export function openGoogleCalendar(event) {
  if (!event) return
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${calendarStamp(event.start)}/${calendarStamp(event.end)}`,
    details: event.description,
    location: event.location,
  })
  window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener,noreferrer')
}

export function downloadAppleCalendar(event) {
  if (!event) return
  const stamp = calendarStamp(new Date())
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Svirr//Hold//DA',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${stamp}-${event.fileName}@svirr`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${calendarStamp(event.start)}`,
    `DTEND:${calendarStamp(event.end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    event.location ? `LOCATION:${escapeIcs(event.location)}` : null,
    event.description ? `DESCRIPTION:${escapeIcs(event.description)}` : null,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n')
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${event.fileName || 'kamp'}.ics`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function formatMessageTime(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  const now = new Date()
  const time = new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' }).format(date)
  if (date.toDateString() === now.toDateString()) return time
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return `i går ${time}`
  const day = new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short' }).format(date)
  return `${day} ${time}`
}

export function formatDay(iso) {
  if (!iso) return 'Ikke sat'
  return new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso))
}

export function memberName(member, youName) {
  if (!member) return 'Ukendt'
  if (member.id === ME) return youName || 'Dig'
  return member.name
}

export function readImage(file, max = 1400) {
  return new Promise((resolve, reject) => {
    const source = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      const scale = Math.min(1, max / Math.max(image.width, image.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(image.width * scale)
      canvas.height = Math.round(image.height * scale)
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)
      URL.revokeObjectURL(source)
      resolve(canvas.toDataURL('image/jpeg', max <= 512 ? 0.82 : 0.72))
    }
    image.onerror = () => {
      URL.revokeObjectURL(source)
      reject(new Error('Billedet kunne ikke læses'))
    }
    image.src = source
  })
}

export function mapsUrl(place, provider = 'google') {
  const query = encodeURIComponent(place)
  if (provider === 'apple') return `https://maps.apple.com/?q=${query}`
  return `https://www.google.com/maps/search/?api=1&query=${query}`
}

const BOX_UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

export function fineBoxIdFrom(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  const fromPath = text.match(new RegExp(`/box/(${BOX_UUID})`, 'i'))
  const raw = fromPath?.[1] || (new RegExp(`^${BOX_UUID}$`, 'i').test(text) ? text : '')
  return raw ? raw.toLowerCase() : null
}

export function fineBoxPayUrl({ boxId, amountKr, message }) {
  const id = fineBoxIdFrom(boxId)
  const kroner = Math.round(Number(amountKr) || 0)
  if (!id || kroner <= 0) return ''
  const params = new URLSearchParams()
  params.set('amount', String(kroner * 100))
  const note = String(message || '').replace(/\s+/g, ' ').trim().slice(0, 60)
  if (note) params.set('message', note)
  return `https://qr.mobilepay.dk/box/${id}/pay-in?${params.toString()}`
}

export function joinSpot(attendees, waitlist, max, playerId) {
  if (attendees.includes(playerId) || waitlist.includes(playerId)) {
    return { attendees, waitlist }
  }
  if (max == null || attendees.length < max) {
    return { attendees: [...attendees, playerId], waitlist }
  }
  return { attendees, waitlist: [...waitlist, playerId] }
}

export function leaveSpot(attendees, waitlist, max, playerId) {
  const wasAttending = attendees.includes(playerId)
  let nextAttendees = attendees.filter((id) => id !== playerId)
  let nextWait = waitlist.filter((id) => id !== playerId)
  if (wasAttending && nextWait.length > 0 && (max == null || nextAttendees.length < max)) {
    const [promoted, ...rest] = nextWait
    nextAttendees = [...nextAttendees, promoted]
    nextWait = rest
  }
  return { attendees: nextAttendees, waitlist: nextWait }
}

export function splitMatches(matches) {
  const now = Date.now()
  const upcoming = []
  const played = []
  matches.forEach((match) => {
    if (new Date(match.kickoff).getTime() < now) played.push(match)
    else upcoming.push(match)
  })
  upcoming.sort((a, b) => new Date(a.kickoff) - new Date(b.kickoff))
  played.sort((a, b) => new Date(b.kickoff) - new Date(a.kickoff))
  return { upcoming, played }
}

export function nextMatch(season) {
  return splitMatches(season.matches).upcoming.find((match) => match.status !== 'cancelled') || null
}

export function reminderMatch(season, playerId) {
  const now = Date.now()
  return splitMatches(season.matches).upcoming.find((match) => {
    if (match.status === 'cancelled') return false
    if (!match.attendees.includes(playerId)) return false
    const until = new Date(match.kickoff).getTime() - now
    return until > 0 && until <= 60 * 60 * 1000
  }) || null
}

export function scorersFor(season) {
  const byPlayer = new Map()
  const played = new Map()
  season.matches.forEach((match) => {
    if (match.status === 'cancelled' || !hasStarted(match.kickoff)) return
    match.attendees.forEach((playerId) => {
      const games = played.get(playerId) || new Set()
      games.add(match.id)
      played.set(playerId, games)
    })
    match.goals.forEach((goal) => {
      const games = played.get(goal.playerId) || new Set()
      games.add(match.id)
      played.set(goal.playerId, games)
      const current = byPlayer.get(goal.playerId) || {
        playerId: goal.playerId,
        goals: 0,
        matches: [],
      }
      current.goals += goal.count
      current.matches.push({
        matchId: match.id,
        opponent: match.opponent,
        count: goal.count,
        kickoff: match.kickoff,
      })
      byPlayer.set(goal.playerId, current)
    })
  })
  return [...byPlayer.values()].map((row) => ({
    ...row,
    played: played.get(row.playerId)?.size || 0,
  }))
}

function person(id, name, isAdmin = false) {
  return { id, name, isAdmin }
}

function match(fields) {
  return {
    home: true,
    meeting: '',
    note: '',
    status: 'scheduled',
    manual: false,
    attendees: [],
    waitlist: [],
    goals: [],
    messages: [],
    ...fields,
  }
}

export function seededNews(teamId) {
  if (teamId === 'svirrens') {
    return [
      {
        id: 'news-frem',
        text: 'Ny kamp mod Frem er oprettet',
        createdAt: atDayHour(-2, 8, 30),
        path: '/hold/svirrens/kamp/b1903',
      },
      {
        id: 'news-traening',
        text: 'Fællestræning er oprettet',
        createdAt: atDayHour(-5, 15, 0),
        path: '/hold/svirrens/arrangement/traening',
      },
      {
        id: 'news-freja',
        text: 'Freja er kommet med på holdet',
        createdAt: atDayHour(-8, 19, 10),
        path: '',
      },
    ]
  }
  if (teamId === 'fredag') {
    return [
      {
        id: 'news-lordag',
        text: 'Ny kamp mod Lørdagsholdet er oprettet',
        createdAt: atDayHour(-3, 12, 0),
        path: '/hold/fredag/kamp/fredag-kamp',
      },
      {
        id: 'news-ida',
        text: 'Ida er kommet med på holdet',
        createdAt: atDayHour(-9, 18, 0),
        path: '',
      },
    ]
  }
  return []
}

export function createInitialState() {
  const squad = [
    person(ME, 'Dig', true),
    person('anders', 'Anders'),
    person('emma', 'Emma'),
    person('jonas', 'Jonas'),
    person('sofie', 'Sofie'),
    person('noah', 'Noah'),
    person('ida', 'Ida'),
    person('lucas', 'Lucas'),
    person('freja', 'Freja'),
    person('oliver', 'Oliver'),
  ]
  const playedSquad = ['anders', 'emma', 'jonas', 'sofie', ME, 'noah']
  const soonSquad = ['anders', 'emma', 'jonas', ME, 'ida', 'lucas']
  const fullSquad = ['anders', 'emma', 'jonas', 'sofie', 'noah', 'ida', 'lucas', 'freja']

  const autumn = {
    id: 'efterar-2026',
    name: 'Efterår 2026',
    current: true,
    fee: {
      amount: 400,
      due: '2026-10-31',
      paid: { [ME]: true, anders: true, emma: true },
    },
    matches: [
      match({
        id: 'ab70',
        opponent: 'AB 70',
        home: true,
        kickoff: atDayHour(-4, 14, 0),
        place: 'Tingbjerg Idrætspark, København',
        meeting: '13:15',
        max: 11,
        attendees: playedSquad,
        goals: [
          { playerId: ME, count: 2 },
          { playerId: 'anders', count: 1 },
          { playerId: 'emma', count: 1 },
        ],
        messages: [
          { id: 'm1', authorId: 'jonas', text: 'Jeg er der 10 minutter før.', createdAt: atDayHour(-4, 12, 0) },
        ],
      }),
      match({
        id: 'fremad',
        opponent: 'Fremad',
        home: false,
        kickoff: atDayHour(-1, 18, 30),
        place: 'Valby Idrætspark, København',
        meeting: '17:45',
        max: 11,
        attendees: ['anders', 'emma', ME, 'sofie', 'noah', 'ida'],
      }),
      match({
        id: 'skovlunde',
        opponent: 'Skovlunde',
        home: true,
        kickoff: hoursFromNow(0.8),
        place: 'Tingbjerg Idrætspark, København',
        meeting: 'Mød 45 minutter før',
        max: 11,
        note: 'Hvide trøjer.',
        attendees: soonSquad,
      }),
      match({
        id: 'aflyst',
        opponent: 'Vanløse',
        home: false,
        kickoff: atDayHour(2, 19, 0),
        place: 'Vanløse Idrætspark, København',
        max: 11,
        status: 'cancelled',
        attendees: ['anders', ME],
      }),
      match({
        id: 'vanloese',
        opponent: 'B 1908',
        home: false,
        kickoff: atDayHour(6, 13, 0),
        place: 'Sundby Idrætspark, København',
        meeting: '12:15',
        max: 8,
        attendees: fullSquad,
        waitlist: ['oliver'],
      }),
      match({
        id: 'b1903',
        opponent: 'Frem',
        home: true,
        kickoff: atDayHour(13, 11, 0),
        place: 'Tingbjerg Idrætspark, København',
        max: 11,
        attendees: ['anders', 'emma', 'jonas'],
      }),
    ],
    events: [
      {
        id: 'traening',
        title: 'Fællestræning',
        kickoff: atDayHour(3, 18, 0),
        place: 'Tingbjerg Idrætspark, København',
        description: 'Tag en bold med, hvis du har en.',
        max: null,
        attendees: [ME, 'anders', 'emma', 'noah'],
        waitlist: [],
      },
      {
        id: 'fest',
        title: 'Afslutningsfest',
        kickoff: atDayHour(21, 18, 0),
        place: 'Klubhuset, Tingbjerg',
        description: 'Mad og drikke. Sig til, hvis du tager en gæst med.',
        max: 24,
        attendees: ['anders', 'emma', 'sofie', 'jonas', ME],
        waitlist: [],
      },
    ],
  }

  const spring = {
    id: 'forar-2026',
    name: 'Forår 2026',
    current: false,
    fee: {
      amount: 350,
      due: '2026-03-01',
      paid: { [ME]: true, anders: true, emma: true, jonas: true, sofie: true },
    },
    matches: [
      match({
        id: 'foraar-1',
        opponent: 'Skjold',
        home: true,
        kickoff: '2026-04-12T12:00:00.000Z',
        place: 'Tingbjerg Idrætspark, København',
        max: 11,
        attendees: playedSquad,
        goals: [
          { playerId: 'anders', count: 2 },
          { playerId: 'jonas', count: 1 },
        ],
      }),
      match({
        id: 'foraar-2',
        opponent: 'Fremad Amager',
        home: false,
        kickoff: '2026-05-03T13:00:00.000Z',
        place: 'Sundby Idrætspark, København',
        max: 11,
        attendees: playedSquad,
        goals: [
          { playerId: ME, count: 1 },
          { playerId: 'emma', count: 2 },
        ],
      }),
    ],
    events: [],
  }

  const fridaySquad = [
    person(ME, 'Dig', false),
    person('anders', 'Anders', true),
    person('ida', 'Ida'),
    person('oliver', 'Oliver'),
  ]

  return {
    previewRole: 'auto',
    viewing: {},
    teams: [
      {
        id: 'svirrens',
        name: 'Svirr IF',
        initials: 'SIF',
        mobilePay: '12345678',
        icalUrl: '',
        inviteCode: 'svirrens-efterar',
        members: squad,
        news: seededNews('svirrens'),
        posts: [
          {
            id: 'p1',
            authorId: 'anders',
            text: 'Husk hvide trøjer til den næste hjemmekamp.',
            createdAt: atDayHour(-1, 9, 0),
            comments: [
              { id: 'c1', authorId: ME, text: 'Tak, de ligger i tasken.', createdAt: atDayHour(-1, 10, 0) },
            ],
          },
          {
            id: 'p2',
            authorId: 'emma',
            text: 'Nogen der har en pumpe med til træning?',
            createdAt: atDayHour(-2, 16, 30),
            comments: [],
          },
        ],
        seasons: [autumn, spring],
      },
      {
        id: 'fredag',
        name: 'Fredagsholdet',
        initials: 'FH',
        mobilePay: '87654321',
        icalUrl: '',
        inviteCode: 'fredagsholdet',
        members: fridaySquad,
        news: seededNews('fredag'),
        posts: [
          {
            id: 'fp1',
            authorId: 'anders',
            text: 'Vi tager en øl efter kampen, hvis nogen er friske.',
            createdAt: atDayHour(-3, 20, 0),
            comments: [],
          },
        ],
        seasons: [
          {
            id: 'fredag-2026',
            name: 'Efterår 2026',
            current: true,
            fee: { amount: 200, due: '2026-10-15', paid: { anders: true } },
            matches: [
              match({
                id: 'fredag-kamp',
                opponent: 'Lørdagsholdet',
                home: true,
                kickoff: atDayHour(4, 17, 0),
                place: 'Nørrebro Stadion, København',
                max: 7,
                attendees: ['anders', 'ida'],
              }),
            ],
            events: [],
          },
        ],
      },
    ],
  }
}
